import { Grid } from '@mui/material';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { hqRoutes } from 'hq/Routes';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header, Page } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { HQProjectFormTypeCreate } from 'shared/components/project-forms/types/project-form.types';
import { CompanyInput } from 'shared/components/shift-forms/components/forms/shared/inputs/company-input';
import { HQProjectPayloadCreate } from 'hq/services/project-planning-service.types';
import { invariant } from 'shared/utils/utils';
import { mapWorkTemplateToDetails } from 'api/services/work-template/work-template.utils';
import { useSiteDetection } from 'shared/hooks';
import { useGetWorkTemplate } from 'api';
import { Companies } from 'api/services/company/company-service.schema';
import { ProjectCreateFormProvider } from './project-create-form-provider';
import { useCreateProject } from '../../hooks/use-create-project';

type ProjectCreateOrDuplicateProps = {
  companies: Companies;
  successMessage: string;
  pageTitle: string;
};

export const ProjectCreateFormPage = ({
  companies,
  successMessage,
  pageTitle,
}: ProjectCreateOrDuplicateProps) => {
  const formMethods = useFormContext<HQProjectFormTypeCreate>();
  const jobId = formMethods.watch('jobId');

  const { site } = useSiteDetection();
  const jobDetailsQuery = useGetWorkTemplate(site, jobId, !!jobId);

  const { alertSuccess, alertError } = useAlert();

  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const mutation = useCreateProject();
  const { search: filterParam } = useLocation();
  const filters = Object.fromEntries(new URLSearchParams(filterParam));

  const company = formMethods.watch('companyOption');
  const companyId = company?.value;

  useEffect(() => {
    if (companyId) {
      formMethods.reset({ companyOption: company }, { keepDefaultValues: true });
    }
  }, [companyId]);

  const handleSubmit = useCallback(() => {
    formMethods.handleSubmit((data: HQProjectFormTypeCreate) => {
      invariant(data.location, 'Location is required');

      const payload: HQProjectPayloadCreate = {
        departmentId: data.departmentId,
        jobId: data.jobId,
        startDate: data.startDate,
        endDate: data.endDate,
        location: data.location,
        weekHourVolume: +data.weeklyHours,
        freelanceProperties: data.isFreelance
          ? {
              hourlyRateCents: Math.round(+data.hourlyRate * 100),
              ortApplicable: data.isFreelanceOrtApplicable,
            }
          : undefined,
        tempWorkProperties: data.isTempwork
          ? {
              ortApplicable: data.isTempworkOrtApplicable,
            }
          : undefined,
      };
      mutation.mutate(payload, {
        onSuccess: (id) => {
          alertSuccess(successMessage);
          navigate(generatePath(hqRoutes.PlannerProject, {}, { projectDetailId: id, ...filters }));
        },
        onError: (e) => alertError(e),
      });
    })();
  }, []);

  const onCancel = useCallback(
    () => navigate(generatePath(hqRoutes.PlannerProject, {}, filters)),
    [generatePath, navigate]
  );

  return (
    <Page
      header={<Header titleText={pageTitle} />}
      toolbox={
        <ToolboxForm
          onSubmit={handleSubmit}
          mutationStatus={mutation.status}
          mode="create"
          onCancel={onCancel}
        />
      }
    >
      <Grid container>
        <Grid item xs={12}>
          <CompanyInput companies={companies} disabled={false}></CompanyInput>
          {companyId ? (
            <ProjectCreateFormProvider
              workDetails={mapWorkTemplateToDetails(jobDetailsQuery.data)}
            />
          ) : null}
        </Grid>
      </Grid>
    </Page>
  );
};
